import { ApolloError } from "@apollo/client";
import { useMemo } from "react";
import { formatISO } from "date-fns";
import { ReportVulnerabilityFieldsFragment, usePublishedReportVulnerabilitiesQuery } from "app/api/graph/types";
import { mapNodes } from "app/util/graph";
import { useFetchAll } from "app/api/graph/hook/use-fetch-all";

type Result = {
    vulnerabilities?: ReportVulnerabilityFieldsFragment[];
    loading: boolean;
    error?: ApolloError;
};

type Filters = {
    dateDiscoveredFrom?: Date;
    dateDiscoveredTo?: Date;
    title?: string;
    cvssScore?: string;
    testingTypes?: string[];
};

export const usePublishedReportVulnerabilities = (filters?: Filters): Result => {
    const { data, loading, error, fetchMore } = usePublishedReportVulnerabilitiesQuery({
        variables: {
            ...(filters && {
                first: 1000,
                title: filters.title,
                dateDiscovered: {
                    after: filters.dateDiscoveredFrom ? formatISO(filters.dateDiscoveredFrom) : undefined,
                    before: filters.dateDiscoveredTo ? formatISO(filters.dateDiscoveredTo) : undefined,
                },
                cvssScore: filters.cvssScore,
                testingTypes: filters.testingTypes,
            }),
        },
        notifyOnNetworkStatusChange: true,
    });
    const vulnerabilities = data?.publishedReportVulnerabilities;
    const pageInfo = data?.publishedReportVulnerabilities?.pageInfo;
    useFetchAll({ loading, pageInfo, fetchMore });

    return useMemo(
        () => ({ vulnerabilities: mapNodes(vulnerabilities, (n) => n), loading, error }),
        [vulnerabilities, loading, error],
    );
};
